import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { UserAccountLevel, UserCredential, UserMetaDataQuery } from 'wb-reddit/graphql/generated';
import { User } from 'wb-reddit/app/routes/teams/utils';
import { translate } from '@aleph/shared-components';
import RedditReportStatCellRenderer from 'wb-reddit/app/routes/reports/create/cellRenderers/stat';
import RedditReportsHeader from 'wb-reddit/app/routes/reports/create/cellRenderers/header';
import DefaultRedditReportsCellRenderer from 'wb-reddit/app/routes/reports/create/cellRenderers/default';
import { SpotifyReportSchedule } from 'wb-spotify/app/common/utils';

export const userHasOneOfRoles = (
	user: UserMetaDataQuery['me'] | undefined | User,
	roles: UserAccountLevel[],
) => roles.includes(user?.userAccountLevel as UserAccountLevel);

export const userHasOneOfCredentials = (
	user: UserMetaDataQuery['me'] | undefined,
	credentials: Partial<UserCredential>[],
) => credentials.some(
	credential => (user?.userCredential ?? []).some(
		userCredential => credential.credentialName === userCredential.credentialName && (
			credential.userCredentialRead === userCredential.userCredentialRead
			|| credential.userCredentialWrite === userCredential.userCredentialWrite
			|| credential.userCredentialDelete === userCredential.userCredentialDelete
		),
	),
);

export const getExtraCreativeContent = (params: ICellRendererParams) => (new Array(6)).fill(null).map((_, idx) => ({
	creativeContentId:             params?.data?.[`creativeContentId${idx}`],
	creativeContentMediaUrl:       params?.data?.[`creativeContentMediaUrl${idx}`]?.mediaUrl,
	creativeContentCallToAction:   params?.data?.[`creativeContentCallToAction${idx}`],
	creativeContentCaption:        params?.data?.[`creativeContentCaption${idx}`],
	creativeContentDestinationUrl: params?.data?.[`creativeContentDestinationUrl${idx}`],
	creativeContentDisplayUrl:     params?.data?.[`creativeContentDisplayUrl${idx}`],
}));

export const mapFormGraphqlErrors = (mutationResponse: Record<string, any>): Record<string, string> => Object.keys(
	mutationResponse?.[0]?.form ?? {},
).reduce((accu, field) => {
	accu[field] = translate(mutationResponse?.[0]?.form?.[field]?.message, {
		defaultValue: 'Unknown error',
	});

	return accu;
}, {});

export const SPOTIFY_REPORTS_DEFAULT_COL_DEF: ColDef = {
	cellClass: params => {
		const currNode = params.node;
		const prevNode = params.api.getDisplayedRowAtIndex((params.node?.rowIndex ?? 0) - 1);

		const isSameAsPrev = currNode?.data?.[params.colDef?.field ?? '']
			=== prevNode?.data?.[params.colDef?.field ?? ''];

		if (isSameAsPrev) {
			return 'report-cell-hidden';
		}

		return null;
	},
	cellRenderer:    RedditReportStatCellRenderer,
	headerComponent: RedditReportsHeader,
	headerClass:     'border-r border-r-gray-300',
	cellStyle:       {
		borderRight: '1px solid hsl(var(--gray-300))',
		padding:     0,
	},
	minWidth: 200,
};

export const SPOTIFY_REPORT_BREAKDOWN_OPTIONS_CATEGORIZED = [
	{ value: 'date', label: 'Date' },
	{ value: 'adName', label: 'Ad' },
	{ value: 'groupName', label: 'Group' },
	{ value: 'campaignName', label: 'Campaign' },
	{ value: 'adAccountName', label: 'Ad account' },
];

export const SPOTIFY_REPORT_BREAKDOWN_OPTIONS_LIFETIME = [
	{ value: 'adName', label: 'Ad' },
	{ value: 'groupName', label: 'Group' },
	{ value: 'campaignName', label: 'Campaign' },
	{ value: 'adAccountName', label: 'Ad account' },
];

export const SPOTIFY_REPORT_COL_DEFS: ColDef[] = [
	{
		field:        'index',
		headerName:   '#',
		valueGetter:  params => (params.node?.rowIndex ?? 0) + 1,
		minWidth:     32,
		maxWidth:     32,
		cellClass:    'text-gray-600 flex items-center justify-center',
		headerClass:  'flex justify-center !px-[4px]',
		pinned:       'left',
		cellRenderer: DefaultRedditReportsCellRenderer,
	},
	{
		field:        'date',
		cellRenderer: DefaultRedditReportsCellRenderer,
		pinned:       'left',
		minWidth:     200,
		maxWidth:     200,
	},
	{
		field:        'adName',
		cellRenderer: DefaultRedditReportsCellRenderer,
		pinned:       'left',
		minWidth:     200,
	},
	{
		field:        'adAccountName',
		cellRenderer: DefaultRedditReportsCellRenderer,
		pinned:       'left',
		minWidth:     200,
	},
	{
		field:        'groupName',
		cellRenderer: DefaultRedditReportsCellRenderer,
		pinned:       'left',
		minWidth:     200,
	},
	{
		field:        'campaignName',
		cellRenderer: DefaultRedditReportsCellRenderer,
		pinned:       'left',
		minWidth:     200,
	},
	{
		field:        'dayOfMonth',
		cellRenderer: DefaultRedditReportsCellRenderer,
		pinned:       'left',
		minWidth:     120,
		maxWidth:     120,
	},
	{
		field:        'weekdayNumber',
		cellRenderer: DefaultRedditReportsCellRenderer,
		pinned:       'left',
		minWidth:     150,
		maxWidth:     150,
	},
	{
		field:        'weekdayName',
		cellRenderer: DefaultRedditReportsCellRenderer,
		pinned:       'left',
		minWidth:     150,
		maxWidth:     150,
	},
	{
		field:        'weekNumber',
		cellRenderer: DefaultRedditReportsCellRenderer,
		pinned:       'left',
		minWidth:     150,
		maxWidth:     150,
	},
	{
		field:        'monthNumber',
		cellRenderer: DefaultRedditReportsCellRenderer,
		pinned:       'left',
		minWidth:     150,
		maxWidth:     150,
	},
	{
		field:        'monthName',
		cellRenderer: DefaultRedditReportsCellRenderer,
		pinned:       'left',
		minWidth:     150,
		maxWidth:     150,
	},
	{
		field:        'quarter',
		cellRenderer: DefaultRedditReportsCellRenderer,
		pinned:       'left',
		minWidth:     100,
		maxWidth:     100,
	},
	{
		field:        'year',
		cellRenderer: DefaultRedditReportsCellRenderer,
		pinned:       'left',
		minWidth:     100,
		maxWidth:     100,
	},
	{
		field:        'yearMonth',
		cellRenderer: DefaultRedditReportsCellRenderer,
		pinned:       'left',
		minWidth:     150,
		maxWidth:     150,
	},
	{
		field:        'country',
		cellRenderer: DefaultRedditReportsCellRenderer,
		pinned:       'left',
		minWidth:     150,
		maxWidth:     150,
	},
	{
		field:        'platform',
		cellRenderer: DefaultRedditReportsCellRenderer,
		pinned:       'left',
		minWidth:     150,
		maxWidth:     150,
	},
	{
		field:        'gender',
		cellRenderer: DefaultRedditReportsCellRenderer,
		pinned:       'left',
		minWidth:     100,
		maxWidth:     100,
	},
	{
		field:        'age',
		cellRenderer: DefaultRedditReportsCellRenderer,
		pinned:       'left',
		minWidth:     100,
		maxWidth:     100,
	},
	{
		field:        'genre',
		cellRenderer: DefaultRedditReportsCellRenderer,
		pinned:       'left',
		minWidth:     150,
		maxWidth:     150,
	},
	{ field: 'externalAdId', cellClass: () => null },
	{ field: 'externalGroupId', cellClass: () => null },
	{ field: 'externalCampaignId', cellClass: () => null },
	{ field: 'externalAdAccountId', cellClass: () => null },
	{ field: 'clicks', cellClass: () => null },
	{ field: 'completes', cellClass: () => null },
	{ field: 'completionRate', cellClass: () => null },
	{ field: 'conversionRate', cellClass: () => null },
	{ field: 'ctr', cellClass: () => null },
	{ field: 'eCpm', cellClass: () => null },
	{ field: 'eCpcl', cellClass: () => null },
	{ field: 'firstQuartiles', cellClass: () => null },
	{ field: 'frequency', cellClass: () => null },
	{ field: 'impressions', cellClass: () => null },
	{ field: 'intentRate', cellClass: () => null },
	{ field: 'listeners', cellClass: () => null },
	{ field: 'midpoints', cellClass: () => null },
	{ field: 'newListeners', cellClass: () => null },
	{ field: 'newListenerConversionRate', cellClass: () => null },
	{ field: 'newListenerStreams', cellClass: () => null },
	{ field: 'offSpotifyImpressions', cellClass: () => null },
	{ field: 'paidListens', cellClass: () => null },
	{ field: 'paidListensFrequency', cellClass: () => null },
	{ field: 'paidListensReach', cellClass: () => null },
	{ field: 'reach', cellClass: () => null },
	{ field: 'skips', cellClass: () => null },
	{ field: 'spend', cellClass: () => null },
	{ field: 'starts', cellClass: () => null },
	{ field: 'streams', cellClass: () => null },
	{ field: 'streamsPerNewListener', cellClass: () => null },
	{ field: 'streamsPerUser', cellClass: () => null },
	{ field: 'thirdQuartiles', cellClass: () => null },
	{ field: 'videoViews', cellClass: () => null },
	{ field: 'videoExpands', cellClass: () => null },
	{ field: 'videoExpandsRate', cellClass: () => null },
	{ field: 'unmutes', cellClass: () => null },
	{ field: 'adCompletes', cellClass: () => null },
	{ field: 'playedTo25', cellClass: () => null },
	{ field: 'playedTo50', cellClass: () => null },
	{ field: 'playedTo75', cellClass: () => null },
	{ field: 'playedTo100', cellClass: () => null },
	{ field: 'skadAppInstalls', cellClass: () => null },
	{ field: 'kochavaAppInstalls', cellClass: () => null },
	{ field: 'appsflyerAppInstalls', cellClass: () => null },
	{ field: 'modeledAddToCart', cellClass: () => null },
	{ field: 'modeledLeads', cellClass: () => null },
	{ field: 'modeledPageViews', cellClass: () => null },
	{ field: 'modeledPurchases', cellClass: () => null },
	{ field: 'unmodeledAddToCart', cellClass: () => null },
	{ field: 'unmodeledLeads', cellClass: () => null },
	{ field: 'unmodeledPageViews', cellClass: () => null },
	{ field: 'unmodeledPurchases', cellClass: () => null },
	{ field: 'listenerConversionRate', cellClass: () => null },
	{ field: 'frequencyOfAdCompletes', cellClass: () => null },
	{ field: 'reachOfAdCompletes', cellClass: () => null },
	{ field: 'cpm', cellClass: () => null },
	{ field: 'cpcl', cellClass: () => null },
	{ field: 'avgStreamsPerListener', cellClass: () => null },
	{ field: 'avgStreamsPerNewListener', cellClass: () => null },
	{ field: 'videoExpandRate', cellClass: () => null },
];

export const SCHEDULER_OPTIONS = [
	{ value: SpotifyReportSchedule.OnlyOnce, label: 'Only once' },
	{ value: SpotifyReportSchedule.EveryMonday, label: 'Every monday' },
	{ value: SpotifyReportSchedule.EveryWeekday, label: 'Every weekday' },
	{ value: SpotifyReportSchedule.FirstOfTheMonth, label: 'First of the month' },
];
